import { getActiveCatalogOwner } from "./catalogScope";
import { getInitData } from "../telegram/webapp";
import { tr, localizeServerMessage } from "../i18n/strings";
import {
  ApiError,
  AuthError,
  NetworkError,
  ReferencedError,
  ThrottleError,
  ValidationError,
  type FieldErrorMap,
} from "./errors";

const BASE = (import.meta.env.VITE_API_BASE ?? "/api/v1").replace(/\/$/, "");

export type Query = Record<
  string,
  string | number | boolean | null | undefined | Array<string | number>
>;

/** Repeats a key per array item — the shape `?medium=a&medium=b` the API expects. */
export function buildQuery(params?: Query): string {
  if (!params) return "";
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === "") continue;
    if (Array.isArray(value)) {
      for (const item of value) search.append(key, String(item));
    } else {
      search.append(key, String(value));
    }
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

/** Normalizes DRF's several 400 body shapes into one field map. */
function toFieldMap(body: unknown): FieldErrorMap {
  if (Array.isArray(body)) return { non_field_errors: body.map(String) };
  if (body && typeof body === "object") {
    const out: FieldErrorMap = {};
    for (const [key, value] of Object.entries(body as Record<string, unknown>)) {
      const list = Array.isArray(value) ? value.map(String) : [String(value)];
      out[key] = list.map((message) => localizeServerMessage(message));
    }
    return out;
  }
  return { detail: [String(body)] };
}

async function readBody(response: Response): Promise<unknown> {
  const type = response.headers.get("content-type") ?? "";
  try {
    return type.includes("json") ? await response.json() : await response.text();
  } catch {
    return null;
  }
}

function raise(status: number, body: unknown): never {
  if (status === 400) {
    const fields = toFieldMap(body);
    throw new ValidationError(fields, Object.values(fields).flat()[0] ?? tr().errors.title);
  }
  if (status === 401) throw new AuthError(tr().errors.unauthorized, 401);
  if (status === 403) throw new AuthError(tr().errors.forbidden, 403);
  if (status === 404) throw new ApiError(tr().errors.notFound, 404);
  if (status === 409) {
    const detail = (body as { referenced_by?: number })?.referenced_by ?? 0;
    throw new ReferencedError(tr().errors.referenced, detail);
  }
  if (status === 429) {
    const wait = Number((body as { detail?: string })?.detail?.match(/\d+/)?.[0] ?? 30);
    throw new ThrottleError(tr().errors.throttled(wait), wait);
  }
  throw new ApiError(tr().errors.server, status);
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  query?: Query;
  signal?: AbortSignal;
}

/** The one place that speaks to the API. Absolute URLs pass straight through. */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, query, signal } = options;
  const url = path.startsWith("http") ? path : `${BASE}${path}${buildQuery(query)}`;

  let response: Response;
  const catalogOwner = getActiveCatalogOwner();
  try {
    response = await fetch(url, {
      method,
      signal,
      headers: {
        Authorization: `tma ${getInitData()}`,
        ...(catalogOwner === null ? {} : { "X-Catalog-Owner": String(catalogOwner) }),
        ...(body === undefined ? {} : { "Content-Type": "application/json" }),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (error) {
    if ((error as Error)?.name === "AbortError") throw error;
    throw new NetworkError(tr().errors.network);
  }

  if (response.status === 204) return undefined as T;
  const payload = await readBody(response);
  if (!response.ok) raise(response.status, payload);
  return payload as T;
}

export const api = {
  get: <T>(path: string, query?: Query, signal?: AbortSignal) =>
    request<T>(path, { query, signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: "POST", body }),
  patch: <T>(path: string, body: unknown) => request<T>(path, { method: "PATCH", body }),
  delete: (path: string) => request<void>(path, { method: "DELETE" }),
};
